import { ProductBranchSlice } from "@/store/BranchSlice";
import { RootState } from "@/store/store";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

type Role = {
    name: string;
    nameAR: string;
    nameEN: string;
    invitable: boolean;
    max: number;
};

export default function AddEmployee() {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const navigate = useNavigate();
    const [roles, setRoles] = useState<Role[]>([]);
    const [email, setEmail] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<ProductBranchSlice[]>([])
    const [selectedBranch, setSelectedBranch] = useState<string>();
    const userData = useSelector((state: RootState) => state.user.userData?.role) || "User";


    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    async function getBranches() {
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/branches/list`,
                method: "GET",
                headers: {
                    Authorization: token,
                },
            };
            const response = await axios.request(options);

            setBranches(response.data.branches);



        } catch (error) {
            console.error(error);
        }
    }
    async function getRoles() {
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/users/roles`, {
                headers: { Authorization: token },
            });
            setRoles(response.data);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    }

    async function handleInvite(e: React.FormEvent) {
        e.preventDefault();
        if (selectedRole === "") {
            toast.error(t("permissions.selectRole"));
            return;
        }
        if (selectedRole === "admin" && !selectedBranch) {
            toast.error(t("permissions.selectBranch"));
            return;
        }
        if (selectedRole !== "superadmin" && userData !== "admin" && !selectedBranch) {
            toast.error(t("permissions.selectBranch"));
            return;

        }
        const toastId = toast.loading(t("orders.loading"));
        setLoading(true);

        try {
            const response = await axios.post(
                `${baseUrl}/api/dashboard/users/invite?locale=${locale}`,
                {
                    email,
                    role: selectedRole,
                    ...(selectedRole !== "superadmin" && userData !== "admin" && { branch: selectedBranch }),
                },
                {
                    headers: { Authorization: token },
                }
            );

            if (response.data.success === true) {
                toast.success(t("permissions.inviteSent"));
                navigate("/settings/permissions");
            } else {
                toast.error(t(response.data.error));
            }
        } catch (error) {
            console.error("Invite error:", error);
            toast.error(t("permissions.inviteFailed"));
        } finally {
            setLoading(false);
            toast.dismiss(toastId);
        }
    }


    useEffect(() => {
        if (!token) return
        getRoles();
        getBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <>
            <div className="my-5">
                <h2 className="text-xl font-semibold">{t("permissions.add")}</h2>
            </div>

            <form onSubmit={handleInvite}>
                <div className="flex flex-col gap-10 text-gray-400">
                    <div className="flex flex-col gap-2 border rounded-md p-5">
                        <h3 className="text-lg font-semibold text-gray-500">{t("permissions.role")}</h3>
                        <label htmlFor="role">{t("permissions.selectRole")}</label>
                        <select
                            name="role"
                            id="role"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="border py-1 rounded-md px-5"

                        >
                            <option value="">{t("permissions.selectFromRole")}</option>
                            {roles
                                .filter((role: Role) => role.invitable)
                                .map((role: Role) => (
                                    <option key={role.name} value={role.name} className="text-sidebar">
                                        {locale === "en" ? role.nameEN : role.nameAR}
                                    </option>
                                ))}
                        </select>
                    </div>
                    {selectedRole !== "superadmin" && userData !== "admin" && branches && (
                        <div className="flex flex-col gap-2 border rounded-md p-5">
                            <h3 className="text-lg font-semibold text-gray-500">{t("permissions.branch")}</h3>
                            <label htmlFor="branch">{t("permissions.selectBranch")}</label>
                            <select
                                name="branch"
                                id="branch"
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                className="border py-1 rounded-md px-5"
                            >
                                <option value="">{t("permissions.branch")}</option>
                                {branches.map((branch) => (
                                    <option key={branch._id} value={branch._id} className="text-sidebar">
                                        {locale === "en" ? branch.nameEN : branch.nameAR}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="flex flex-col gap-2 border rounded-md p-5">
                        <h3 className="text-lg font-semibold text-gray-500">{t("permissions.sendInviteLink")}</h3>
                        <label htmlFor="email">{t("permissions.email")}</label>
                        <input
                            type="email"
                            name="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="border text-sidebar py-1 rounded-md px-5"
                            required
                        />
                    </div>

                    <button type="submit" className="my-btn" disabled={loading}>
                        {t("permissions.sendInvite")}
                    </button>


                </div>
            </form>
        </>
    );
}
